<template>
  <Breadcrumb :items="breadcrumbs" />

  <div class="ds-page">
    <div class="ds-container ds-container--wide">
      <div class="ds-page-header">
        <div>
          <h1 class="ds-page-header__title">تقارير الاشتراكات</h1>
          <p class="ds-page-header__subtitle">نظرة عامة على الإيرادات والاشتراكات والمدفوعات</p>
        </div>
      </div>

      <Loading v-if="loading" />

      <template v-else>
        <!-- KPI cards -->
        <div class="kpi-grid mb-4">
          <div v-for="card in cards" :key="card.label" class="kpi-card" :class="card.cls">
            <div class="kpi-card__icon"><i :class="card.icon"></i></div>
            <div class="kpi-card__body">
              <div class="kpi-card__value">{{ card.value }}</div>
              <div class="kpi-card__label">{{ card.label }}</div>
            </div>
          </div>
        </div>

        <div class="report-grid">
          <!-- Revenue by plan -->
          <div class="ds-card">
            <div class="ds-card__header">
              <div class="ds-card__header-icon"><i class="pi pi-chart-bar"></i></div>
              <p class="ds-card__title">الإيراد حسب الخطة</p>
            </div>
            <div class="table-responsive">
              <table class="table align-middle mb-0">
                <thead><tr><th class="ps-3">الخطة</th><th class="text-center">العدد</th><th class="text-center pe-3">الإيراد</th></tr></thead>
                <tbody>
                  <tr v-for="row in dashboard.revenue_by_plan" :key="row.plan_type">
                    <td class="ps-3">{{ planLabel(row.plan_type) }}</td>
                    <td class="text-center">{{ row.count }}</td>
                    <td class="text-center pe-3"><PriceBadge :value="row.revenue" /></td>
                  </tr>
                  <tr v-if="!dashboard.revenue_by_plan.length"><td colspan="3" class="text-center text-muted py-4">لا توجد بيانات</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Revenue by product -->
          <div class="ds-card">
            <div class="ds-card__header">
              <div class="ds-card__header-icon"><i class="pi pi-chart-pie"></i></div>
              <p class="ds-card__title">الإيراد حسب المنتج</p>
            </div>
            <div class="table-responsive">
              <table class="table align-middle mb-0">
                <thead><tr><th class="ps-3">النوع</th><th class="text-center">العدد</th><th class="text-center pe-3">الإيراد</th></tr></thead>
                <tbody>
                  <tr v-for="row in dashboard.revenue_by_product" :key="row.type">
                    <td class="ps-3">{{ typeLabel(row.type) }}</td>
                    <td class="text-center">{{ row.count }}</td>
                    <td class="text-center pe-3"><PriceBadge :value="row.revenue" /></td>
                  </tr>
                  <tr v-if="!dashboard.revenue_by_product.length"><td colspan="3" class="text-center text-muted py-4">لا توجد بيانات</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          <template v-if="reports">
            <!-- Monthly revenue -->
            <div class="ds-card">
              <div class="ds-card__header">
                <div class="ds-card__header-icon"><i class="pi pi-calendar"></i></div>
                <div>
                  <p class="ds-card__title">الإيراد الشهري</p>
                  <p class="ds-card__subtitle">الإجمالي: {{ money(reports.total_revenue) }}</p>
                </div>
              </div>
              <div class="table-responsive">
                <table class="table align-middle mb-0">
                  <thead><tr><th class="ps-3">الشهر</th><th class="text-center pe-3">الإيراد</th></tr></thead>
                  <tbody>
                    <tr v-for="row in reports.monthly_revenue" :key="row.month">
                      <td class="ps-3">{{ row.month }}</td>
                      <td class="text-center pe-3"><PriceBadge :value="row.revenue" /></td>
                    </tr>
                    <tr v-if="!reports.monthly_revenue.length"><td colspan="2" class="text-center text-muted py-4">لا توجد بيانات</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Most subscribed courses -->
            <div class="ds-card">
              <div class="ds-card__header">
                <div class="ds-card__header-icon"><i class="pi pi-book"></i></div>
                <p class="ds-card__title">الأكثر اشتراكاً — كورسات</p>
              </div>
              <div class="table-responsive">
                <table class="table align-middle mb-0">
                  <thead><tr><th class="ps-3">الكورس</th><th class="text-center pe-3">عدد المشتركين</th></tr></thead>
                  <tbody>
                    <tr v-for="row in reports.most_subscribed_courses" :key="'c'+row.id">
                      <td class="ps-3">{{ row.label || '—' }}</td>
                      <td class="text-center pe-3">{{ row.subscribers }}</td>
                    </tr>
                    <tr v-if="!reports.most_subscribed_courses.length"><td colspan="2" class="text-center text-muted py-4">لا توجد بيانات</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Most subscribed years -->
            <div class="ds-card">
              <div class="ds-card__header">
                <div class="ds-card__header-icon"><i class="pi pi-graduation-cap"></i></div>
                <p class="ds-card__title">الأكثر اشتراكاً — سنوات دراسية</p>
              </div>
              <div class="table-responsive">
                <table class="table align-middle mb-0">
                  <thead><tr><th class="ps-3">السنة</th><th class="text-center pe-3">عدد المشتركين</th></tr></thead>
                  <tbody>
                    <tr v-for="row in reports.most_subscribed_years" :key="'y'+row.id">
                      <td class="ps-3">{{ row.label || '—' }}</td>
                      <td class="text-center pe-3">{{ row.subscribers }}</td>
                    </tr>
                    <tr v-if="!reports.most_subscribed_years.length"><td colspan="2" class="text-center text-muted py-4">لا توجد بيانات</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Pending / expired summary -->
            <div class="ds-card">
              <div class="ds-card__header">
                <div class="ds-card__header-icon"><i class="pi pi-exclamation-circle"></i></div>
                <p class="ds-card__title">متابعة</p>
              </div>
              <div class="ds-card__body follow-up-list">
                <router-link to="/payment-requests" class="follow-up-row">
                  <span>مدفوعات قيد المراجعة</span>
                  <span class="status-badge status-badge--pending">{{ reports.pending_payments }}</span>
                </router-link>
                <router-link to="/subscriptions" class="follow-up-row">
                  <span>اشتراكات منتهية</span>
                  <span class="status-badge status-badge--rejected">{{ reports.expired_subscriptions }}</span>
                </router-link>
              </div>
            </div>
          </template>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';
import PriceBadge from '@/views/courses/PriceBadge.vue';
import { useSubscriptionReportStore } from '@/stores/useSubscriptionReportStore.js';
import { planLabel, typeLabel, formatMoney } from '@/composables/subscriptionMeta.js';

export default {
  name: 'SubscriptionReports',
  components: { Breadcrumb, Loading, PriceBadge },
  data() {
    return {
      breadcrumbs: [{ name: 'الرئيسية', path: '/' }, { name: 'تقارير الاشتراكات', path: '/subscription-reports' }],
    };
  },
  computed: {
    reportStore() { return useSubscriptionReportStore(); },
    loading() { return this.reportStore.loadingDashboard && !this.reportStore.dashboard; },
    dashboard() { return this.reportStore.dashboard || { revenue_by_plan: [], revenue_by_product: [] }; },
    reports() { return this.reportStore.reports; },
    cards() {
      const d = this.dashboard;
      return [
        { label: 'اشتراكات مفعّلة', value: d.active_subscriptions ?? 0, icon: 'pi pi-check-circle', cls: 'kpi-card--green' },
        { label: 'اشتراكات منتهية', value: d.expired_subscriptions ?? 0, icon: 'pi pi-times-circle', cls: 'kpi-card--red' },
        { label: 'مدفوعات قيد المراجعة', value: d.pending_payments ?? 0, icon: 'pi pi-clock', cls: 'kpi-card--amber' },
        { label: 'تجديدات اليوم', value: d.todays_renewals ?? 0, icon: 'pi pi-calendar', cls: 'kpi-card--blue' },
      ];
    },
  },
  methods: {
    planLabel, typeLabel,
    money: formatMoney,
  },
  mounted() {
    this.reportStore.fetchDashboard();
    this.reportStore.fetchReports();
  },
};
</script>

<style scoped>
.report-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}
@media (max-width: 991.98px) {
  .report-grid { grid-template-columns: 1fr; }
}
.follow-up-list { display: flex; flex-direction: column; gap: 0.5rem; }
.follow-up-row {
  display: flex; align-items: center; justify-content: space-between; gap: 1rem;
  padding: 0.75rem 0.875rem; border-radius: var(--radius-md); background: var(--surface-hover);
  text-decoration: none !important; color: var(--text-primary) !important; font-size: 0.875rem; font-weight: 600;
  transition: background var(--ease-fast);
}
.follow-up-row:hover { background: var(--primary-light); }
</style>
